import React, { useEffect, useRef } from "react";

/**
 * Weicher Lichtschein, der der Maus über einer Sektion folgt (Premium-Agentur-Website-Effekt).
 * Bewegt sich per direkter DOM-Manipulation über eine ref, NICHT über React-State - sonst würde
 * jede Mausbewegung einen Re-Render der ganzen Seite auslösen und ruckeln.
 *
 * Der Listener hängt bewusst am window, nicht am eigenen Container: der Spotlight-Container liegt
 * im DOM hinter anderen absolut positionierten Geschwister-Elementen (Titel, Button) - ein Listener
 * direkt am Container würde deren Mausbewegungen nie mitbekommen, da mousemove nicht zu
 * dahinterliegenden Geschwistern durchgereicht wird. Über window funktioniert es unabhängig davon.
 */
export function CursorSpotlight({ color = "184,134,59", size = 500, opacity = 0.18 }) {
  const dotRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const dot = dotRef.current;
    const container = containerRef.current;
    if (!dot || !container) return;
    if (window.matchMedia && window.matchMedia("(pointer: coarse)").matches) return; // Touch-Geräte: kein Effekt

    let raf = null;
    const onMove = (e) => {
      const rect = container.getBoundingClientRect();
      const inside = e.clientY >= rect.top && e.clientY <= rect.bottom && e.clientX >= rect.left && e.clientX <= rect.right;
      if (!inside) { dot.style.opacity = "0"; return; }
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        dot.style.transform = `translate(${x - size / 2}px, ${y - size / 2}px)`;
        dot.style.opacity = "1";
      });
    };
    const onLeaveWindow = () => { dot.style.opacity = "0"; };

    window.addEventListener("mousemove", onMove);
    document.addEventListener("mouseleave", onLeaveWindow);
    return () => {
      window.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseleave", onLeaveWindow);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [size]);

  return (
    <div ref={containerRef} className="absolute inset-0 overflow-hidden pointer-events-none z-[1]">
      <div
        ref={dotRef}
        className="absolute top-0 left-0 rounded-full"
        style={{
          width: size,
          height: size,
          background: `radial-gradient(circle, rgba(${color},${opacity}) 0%, rgba(${color},0) 70%)`,
          opacity: 0,
          transition: "opacity 0.4s ease",
          willChange: "transform",
        }}
      />
    </div>
  );
}
